import { CardBody, Col, Container, Input, Card } from "reactstrap";
import React, { useContext, useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./StudentList.scss";
import { H5, Image, Btn } from "../../../AbstractElements";
import { getStudents } from "../../../Api/apiUtils";
import AddStudent from "./AddStudent/addStudent";
import { isTeacherLeaderDisabled } from "../../../utils/permissions";
import ClickToCall from "../../../Telephony/ClickToCall";
import { ConfigContext } from "../../../Api/global/ApiContext";

interface Student {
  studentId: string;
  credentialId: string;
  userId: any;
  studentName: string;
  emailId: string;
  mobileNumber: string;
  profilePhoto?: string;
  countryCode: any;
}
type SearchPayload = {
  name?: string;
  emailId?: string;
  mobileNumber?: string;
  studentId?: string;
  userId?: string;
  userType?: string;
  age?: string;
  gender?: string;
  region?: string;
};

const FALLBACK_AGE_OPTIONS = ["6-12", "13-21", "22-30", "31-40", "40+"];

const REGION_OPTIONS = ["Domestic", "International"];

function toAgeFilterValue(name: string): string {
  return String(name || "")
    .replace(/\s*years?/i, "")
    .trim();
}

const StudentPageContainer: React.FC = () => {
  const [students, setStudents] = useState<Student[]>([]);
  const [searchQuery, setSearchQuery] = useState<any>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [noResults, setNoResults] = useState<boolean>(false);
  const navigate = useNavigate();
  const [userTypeFilter, setUserTypeFilter] = useState<string>("ALL"); // "ALL", "PAID", or "DEMO"
  const [ageFilter, setAgeFilter] = useState<string>("");
  const [genderFilter, setGenderFilter] = useState<string>("");
  const [regionFilter, setRegionFilter] = useState<string>("");
  const [loadingMore, setLoadingMore] = useState(false);
  const tlDisabled = isTeacherLeaderDisabled();
  const config = useContext(ConfigContext);
  const searchQueryRef = useRef(searchQuery);
  searchQueryRef.current = searchQuery;
  const pageRef = useRef(0);
  const hasMoreRef = useRef(true);
  const loadingMoreRef = useRef(false);
  const PAGE_SIZE = 50;

  const ageGroups = config?.ageGroups;
  const genders = config?.genders;
  const ageOptions = (Array.isArray(ageGroups) ? ageGroups : [])
    .map((ageGroup: any) => toAgeFilterValue(ageGroup?.name || ageGroup?.localizedName || ""))
    .filter(Boolean);
  const displayedAgeOptions = ageOptions.length ? ageOptions : FALLBACK_AGE_OPTIONS;
  const genderOptions = (Array.isArray(genders) ? genders : [])
    .map((gender: any) => String(gender?.name || "").trim())
    .filter(Boolean);
  
  // Debounce function
  const debounce = (func: Function, delay: number) => {
    let timer: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timer);
      timer = setTimeout(() => func.apply(this, args), delay);
    };
  };

  const buildSearchPayload = useCallback((rawQuery: string): SearchPayload => {
    let searchPayload: SearchPayload = {};
    const query = String(rawQuery ?? "").trim();

    if (query !== "") {
      if (userTypeFilter === "STUDENTID") {
        searchPayload = { studentId: query };
      } else if (userTypeFilter === "STUDENTUSERID") {
        searchPayload = { userId: query };
      } else if (/[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,15}/gi.test(query)) {
        searchPayload = { emailId: query };
      } else if (/^\+?\d+$/.test(query)) {
        const mobileNumber = query.replace(/^\+91|^\+1|^\+44/, "").replace(/\D/g, "");
        searchPayload = { mobileNumber };
      } else {
        searchPayload = { name: query };
      }
    }

    if (userTypeFilter !== "ALL" && userTypeFilter !== "STUDENTID" && userTypeFilter !== "STUDENTUSERID") {
      searchPayload.userType = userTypeFilter;
    }
    if (ageFilter) searchPayload.age = ageFilter;
    if (genderFilter) searchPayload.gender = genderFilter;
    if (regionFilter) searchPayload.region = regionFilter;

    return searchPayload;
  }, [userTypeFilter, ageFilter, genderFilter, regionFilter]);

  const fetchStudents = async (
    searchPayload: SearchPayload = {},
    pageToLoad = 0,
    append = false
  ) => {
    if (append) {
      if (loadingMoreRef.current || !hasMoreRef.current) return;
      loadingMoreRef.current = true;
      setLoadingMore(true);
    } else {
      setLoading(true);
      hasMoreRef.current = true;
      pageRef.current = 0;
    }
    try {
      const fetchedStudents = await getStudents(pageToLoad, PAGE_SIZE, searchPayload);
      if (Array.isArray(fetchedStudents)) {
        setStudents((prev) => (append ? [...prev, ...fetchedStudents] : fetchedStudents));
        setNoResults(!append && fetchedStudents.length === 0);
        hasMoreRef.current = fetchedStudents.length === PAGE_SIZE;
        pageRef.current = pageToLoad;
      } else {
        console.error("Fetched data is not an array:", fetchedStudents);
        if (!append) setNoResults(true);
        hasMoreRef.current = false;
      }
    } catch (error) {
      console.error("There was an error fetching the student data!", error);
      if (!append) setNoResults(true);
      hasMoreRef.current = false;
    } finally {
      setLoading(false);
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  };

  const handleListScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight < el.scrollHeight - 80) return;
    if (!hasMoreRef.current || loadingMoreRef.current || loading) return;
    fetchStudents(buildSearchPayload(searchQueryRef.current), pageRef.current + 1, true);
  };

  const handleFilterChange = (type: string) => {
    setUserTypeFilter(type);
  };
  
  const debouncedSearch = useCallback(
    debounce(async (rawQuery: string) => {
      await fetchStudents(buildSearchPayload(rawQuery));
    }, 500),
    [buildSearchPayload]
  );

  useEffect(() => {
    fetchStudents(buildSearchPayload(searchQueryRef.current));
  }, [buildSearchPayload]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    debouncedSearch(value);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      debouncedSearch(searchQuery);
    }
  };

  const handleCardClick = (student: Student) => {
    if (student.credentialId && student.studentId) {
      navigate(`/pages/student-profile/${student.credentialId}/${student.studentId}`);
    } else {
      console.error("Missing credentialId or studentId");
    }
  };

  function isValidImageUrl(url: string): boolean {
    if (!url) return false;
    try {
      new URL(url);
      return /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(url);
    } catch {
      return false;
    }
  }
  
  function getDisplayContent(student: any): string {
    if (student.studentName && student.studentName.trim().length > 0) {
      return student.studentName.trim().charAt(0).toUpperCase();
    }
    
    if (student.profilePhoto && typeof student.profilePhoto === 'string') {
      return student.profilePhoto.trim().charAt(0).toUpperCase();
    }
    
    return "U";
  }

  return (
    <div>
      <Container fluid >
        <div
          className="d-flex align-items-center mb-4"
          style={{ gap: "12px", flexWrap: "nowrap" }}
        >
          <Input
            type="select"
            value={userTypeFilter}
            onChange={(e) => handleFilterChange(e.target.value)}
            style={{ width: "180px", minWidth: "180px" }}
          >
            <option value="ALL">All Students</option>
            <option value="PAID">Paid Students</option>
            <option value="DEMO">Demo Students</option>
            <option value="STUDENTID">Student ID</option>
            <option value="STUDENTUSERID">Student User ID</option>
          </Input>
          <Input
            type="select"
            value={ageFilter}
            onChange={(e) => setAgeFilter(e.target.value)}
            style={{ width: "130px", minWidth: "130px" }}
          >
            <option value="">All ages</option>
            {displayedAgeOptions.map((age) => (
              <option key={age} value={age}>
                {age}
              </option>
            ))}
          </Input>
          <Input
            type="select"
            value={genderFilter}
            onChange={(e) => setGenderFilter(e.target.value)}
            style={{ width: "150px", minWidth: "150px" }}
          >
            <option value="">All genders</option>
            {(genderOptions.length ? genderOptions : ["Female", "Male"]).map((gender) => (
              <option key={gender} value={gender}>
                {gender}
              </option>
            ))}
          </Input>
          <Input
            type="select"
            value={regionFilter}
            onChange={(e) => setRegionFilter(e.target.value)}
            style={{ width: "160px", minWidth: "160px" }}
          >
            <option value="">All regions</option>
            {REGION_OPTIONS.map((region) => (
              <option key={region} value={region}>
                {region}
              </option>
            ))}
          </Input>
          <Input
            type="text"
            placeholder={
              userTypeFilter === "STUDENTID" ? "Search by Student ID..." :
              userTypeFilter === "STUDENTUSERID" ? "Search by User ID..." :
              "Search by name, email, or mobile number..."
            }
            value={searchQuery}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            style={{ flex: 1, minWidth: "180px" }}
          />
        </div>
        <Col xs="10" className="mb-3">
          <Btn
            color="primary"
            disabled={tlDisabled}
            title={tlDisabled ? "Disabled for Teacher Leaders" : undefined}
            onClick={() => {
              if (tlDisabled) return;
              navigate("/pages/AddStudent");
            }}
          >
            Add New Student
          </Btn>
        </Col>
        {loading ? (
          <div className="text-center">
            <p>Loading students...</p>
          </div>
        ) : noResults ? (
          <div className="text-center">
            <p>No students found matching your search criteria.</p>
          </div>
        ) : (
          <Card style={{ maxHeight: "70vh", overflowY: "auto" }} onScroll={handleListScroll}>
            <div className="email-right-aside">
              <div className="email-body email-list show card">
                <div className="mail-body-wrapper">
                  <ul className="simple-list list-group">
                    {students.map((student, index) => (
                      <li
                        className="inbox-data list-group-item d-flex align-items-center"
                        key={`${student.studentId || student.userId || "student"}-${index}`}
                        onClick={() => handleCardClick(student)}
                      >
                        <div className="student-avatar" aria-hidden>
                          {student.profilePhoto && isValidImageUrl(student.profilePhoto) ? (
                            <img
                              src={student.profilePhoto}
                              className="student-avatar__img"
                              alt=""
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                                const fallback = target.nextElementSibling as HTMLElement;
                                if (fallback) fallback.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div
                            className={`student-avatar__initial${
                              student.profilePhoto && isValidImageUrl(student.profilePhoto) ? ' student-avatar__initial--hidden' : ''
                            }`}
                          >
                            {getDisplayContent(student)}
                          </div>
                        </div>
                        <div style={{ flex: 1, display: "flex", alignItems: "center", gap: "20px", marginLeft: "30px" }}>
                          <div style={{ minWidth: "300px" }}>
                            <b>{student.studentName}</b>
                          </div>
                          <div style={{ minWidth: "350px" }}>
                            {student.emailId || "-"}
                          </div>
                          <div style={{ minWidth: "150px" }}>
                            <ClickToCall
                              number={
                                student.mobileNumber
                                  ? `${student.countryCode || ""}${student.mobileNumber}`
                                  : undefined
                              }
                              name={student.studentName}
                              sourceRecordId={student.studentId}
                              customerUserId={student.userId ? String(student.userId) : undefined}
                            />
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                  {loadingMore ? (
                    <p className="text-center py-3 mb-0">Loading more students...</p>
                  ) : null}
                </div>
              </div>
            </div>
          </Card>
        )}
      </Container>
    </div>
  );
};

export default StudentPageContainer;